import { DateField, Field } from '../PreviewFields';
import { PreviewHeader } from '../PreviewHeader';

// ── Voter ID ──
export interface VoterFormValues {
  epicNo: string;
}
export function VoterIdScreen({
  values,
  onChange,
  epicError,
}: {
  values?: VoterFormValues;
  onChange?: (patch: Partial<VoterFormValues>) => void;
  epicError?: string;
}) {
  return (
    <>
      <PreviewHeader title="Voter ID Verification" subtitle="Enter your EPIC number" />
      <Field
        label="EPIC Number"
        placeholder="ABC1234567"
        hint="10-character Voter ID / EPIC number"
        value={values?.epicNo}
        onChange={onChange ? (v) => onChange({ epicNo: v.toUpperCase() }) : undefined}
        error={epicError}
      />
    </>
  );
}

// ── Passport ──
export interface PassportFormValues {
  name: string;
  passportNo: string;
  fileNo: string;
  dob: string;
  doi: string;
}
export function PassportScreen({
  values,
  onChange,
  errors,
}: {
  values?: PassportFormValues;
  onChange?: (patch: Partial<PassportFormValues>) => void;
  errors?: Partial<Record<keyof PassportFormValues, string>>;
}) {
  return (
    <>
      <PreviewHeader title="Passport Verification" subtitle="Enter your passport details" />
      <Field
        label="Full Name"
        placeholder="John Doe"
        value={values?.name}
        onChange={onChange ? (v) => onChange({ name: v }) : undefined}
        error={errors?.name}
      />
      <Field
        label="Passport Number"
        placeholder="S3733862"
        hint="1 letter + 7 digits"
        value={values?.passportNo}
        onChange={onChange ? (v) => onChange({ passportNo: v.toUpperCase() }) : undefined}
        error={errors?.passportNo}
      />
      <Field
        label="File Number"
        placeholder="BO3072344560818"
        value={values?.fileNo}
        onChange={onChange ? (v) => onChange({ fileNo: v }) : undefined}
        error={errors?.fileNo}
      />
      {/* No `hint="DD/MM/YYYY"` on either date: the placeholder already reads
          DD/MM/YYYY, so the hint restated it one line lower. Five fields make this the
          tallest applicant form we have, and those two lines were the difference
          between fitting a 568px phone (iPhone SE, small Androids) and overflowing it
          by ~15px. The one hint kept — "1 letter + 7 digits" — says something the
          placeholder cannot. */}
      <DateField
        label="Date of Birth"
        required
        value={values?.dob}
        onChange={onChange ? (v) => onChange({ dob: v }) : undefined}
        error={errors?.dob}
      />
      <DateField
        label="Date of Issue"
        required
        value={values?.doi}
        onChange={onChange ? (v) => onChange({ doi: v }) : undefined}
        error={errors?.doi}
      />
    </>
  );
}

// ── Driving Licence ──
export interface DrivingLicenceFormValues {
  dlNo: string;
  dob: string;
}
export function DrivingLicenceScreen({
  values,
  onChange,
  errors,
}: {
  values?: DrivingLicenceFormValues;
  onChange?: (patch: Partial<DrivingLicenceFormValues>) => void;
  errors?: Partial<Record<keyof DrivingLicenceFormValues, string>>;
}) {
  return (
    <>
      <PreviewHeader title="Driving Licence Verification" subtitle="Enter your licence details" />
      {/*
        The designer's own card artwork, used exactly as their
        `DrivingLicenseVerificationScreen` does — same classes, so it renders identically
        in the builder preview AND the live session.

        The source file is 14 MB: Figma embedded a 4000x2666 photograph for a box that
        displays at 56x59 px. `public/driving-licence-card.svg` is that same file with the
        bitmap re-encoded at 600px (still ~5x on a 3x-DPR phone) and Figma's 6-decimal
        path coordinates trimmed to 2 — the card is 320 units wide, so 2 decimals is far
        below one device pixel. Every `width`/`height` attribute and pattern transform is
        untouched, so the geometry and crop are the designer's. 14 MB -> 527 KB, 155 KB
        over the wire.

        Served from `public/` rather than imported, matching the other card assets
        (/pan-card-photo.png, /pan-card-qr.png) — an import would inline it into the JS
        bundle and make it a render-blocking cost on every screen, not just this one.
      */}
      <div className="mb-3">
        <img
          src="/driving-licence-card.svg"
          alt=""
          className="h-auto w-full rounded-3xl border border-[#E5E7EB] object-contain"
        />
      </div>
      <Field
        label="License Number"
        placeholder="Enter License Number"
        value={values?.dlNo}
        onChange={onChange ? (v) => onChange({ dlNo: v.toUpperCase() }) : undefined}
        error={errors?.dlNo}
      />
      <DateField
        label="Date of Birth"
        required
        value={values?.dob}
        onChange={onChange ? (v) => onChange({ dob: v }) : undefined}
        error={errors?.dob}
      />
    </>
  );
}
